/**
 * AnyProfessionalAxisBadges.js
 *
 * Modo "Profissionais": entradas da lista de espera para "Qualquer profissional"
 * viram um selo só por horário, no eixo de horas (fica à vista ao rolar para o
 * lado), em vez de um selo em cada coluna livre. O clique abre o popup do horário
 * com as entradas e a escolha da agenda (slot com `anyProfessional: true`).
 *
 * Usado pelo eixo de horas do Calendar (desktop) e do CalendarMobile.
 */

import React from 'react';
import { Tooltip } from '@mui/material';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AppointmentsState from '../../../consts/AppointmentsState';
import styles from './AnyProfessionalAxisBadges.module.css';

const BADGE_HEIGHT = 14;
// Abaixo do rótulo da hora cheia ("08:00" no topo do horário)
const BELOW_HOUR_LABEL = 15;

const pad = (n) => String(n).padStart(2, '0');

export default function AnyProfessionalAxisBadges({
  date,
  startHour,
  endHour,
  slotHeight,
  slotIntervalMinutes,
  opportunityAt,
  onSelect,
  compact = false,
}) {
  if (!opportunityAt || !onSelect || !(date instanceof Date)) return null;

  const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const slotHeightPx = slotHeight * (slotIntervalMinutes / 60);
  const badges = [];

  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += slotIntervalMinutes) {
      const opportunity = opportunityAt(h, m);
      if (!opportunity) continue;
      const slotTop = (h - startHour) * slotHeight + (m / 60) * slotHeight;
      const top = m === 0
        ? slotTop + Math.max(BELOW_HOUR_LABEL, slotHeightPx - BADGE_HEIGHT - 2)
        : slotTop + Math.max(0, (slotHeightPx - BADGE_HEIGHT) / 2);
      const count = opportunity.patientCount;
      const label = `${count} na lista de espera para qualquer profissional às ${pad(h)}:${pad(m)}`;

      const handleClick = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const slotRect = { left: rect.right, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height };
        onSelect(
          { status: AppointmentsState.Available, date: `${dateStr} ${pad(h)}:${pad(m)}:00`, anyProfessional: true },
          { clientX: slotRect.left, clientY: slotRect.top, _slotRect: slotRect },
        );
      };

      badges.push(
        <Tooltip key={`any-${h}-${m}`} title={label} placement="right" arrow>
          <button
            type="button"
            className={`${styles.badge} ${compact ? styles.badgeCompact : ''} ${opportunity.avgScore >= 65 ? styles.badgeHigh : ''}`}
            style={{ top }}
            onClick={handleClick}
            aria-label={label}
          >
            {!compact && <HourglassEmptyOutlinedIcon className={styles.icon} />}
            <span>{count}</span>
          </button>
        </Tooltip>,
      );
    }
  }

  return badges;
}
